import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Paperclip, Lock, Flag, Pencil, Trash2, ChevronDown, Users, X, MessageSquare, ArrowLeft, Search } from 'lucide-react'; // Lock kept for private message badge display
import api from '../api/client';
import useAuthStore from '../store/authStore';

// ─── Theme ──────────────────────────────────────────────
const T = {
  panelBg:       '#1c1008',
  panelBorder:   '#5a3820',
  surface1:      '#251408',
  surface2:      '#2a1a0a',
  outgoingBg:    'linear-gradient(135deg, #4a2e10, #321f08)',
  outgoingBorder:'#6b4520',
  inputBg:       '#251408',
  inputBorder:   '#5a3820',
  inputFocus:    '#c9a84c',
  textPrimary:   '#f5f0e8',
  textMuted:     '#8a6540',
  textGold:      '#c9a84c',
  privateBg:     '#1a1200',
  privateBorder: '#4a3800',
  urgentBorder:  '#7a2424',
  urgentBg:      '#1e0a0a',
  mentionBg:     '#2a1a0a',
  mentionText:   '#c9a84c',
};

// ─── Helpers ────────────────────────────────────────────
const AVATAR_COLORS = ['#7a5230','#5a7a30','#30607a','#7a3050','#6a6030','#30706a','#704a30','#4a5a7a'];
function getUserColor(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = ((h << 5) - h) + id.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function getInitials(first, last) {
  return `${(first || '?')[0]}${(last || '')[0]}`.toUpperCase();
}

function fmtTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  const h = dt.getHours(), m = dt.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtDateSep(d) {
  const dt = new Date(d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const diff = (today - msgDay) / 86400000;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtFileSize(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}

function renderContent(content) {
  if (!content) return null;
  const parts = [];
  let last = 0;
  const re = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let m;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(
      <span key={m.index} style={{ color: T.mentionText, background: T.mentionBg, padding: '0 3px', borderRadius: 3, fontWeight: 600 }}>
        @{m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  // Linkify URLs
  return parts.map((p, i) => {
    if (typeof p !== 'string') return p;
    const segs = p.split(/(https?:\/\/[^\s]+)/g);
    return segs.map((seg, j) =>
      /^https?:\/\//.test(seg)
        ? <a key={`${i}-${j}`} href={seg} target="_blank" rel="noopener noreferrer" style={{ color: '#a8dff0', textDecoration: 'underline', wordBreak: 'break-all' }}>{seg}</a>
        : <span key={`${i}-${j}`}>{seg}</span>
    );
  });
}

// ─── Subcomponents ──────────────────────────────────────

function Avatar({ userId, first, last, size = 28 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: getUserColor(userId),
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      border: `1px solid ${T.panelBorder}`,
    }}>
      <span style={{ color: '#f5f0e8', fontSize: size * 0.36, fontWeight: 600 }}>{getInitials(first, last)}</span>
    </div>
  );
}

function MentionDropdown({ members, filter, onSelect }) {
  const filtered = members.filter(m => {
    const name = `${m.first_name} ${m.last_name}`.toLowerCase();
    return name.includes((filter || '').toLowerCase());
  }).slice(0, 8);
  if (!filtered.length) return null;
  return (
    <div style={{
      position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, width: 260,
      background: T.surface1, border: `1px solid ${T.panelBorder}`, borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 50, maxHeight: 200, overflowY: 'auto',
    }}>
      {filtered.map(m => (
        <button key={m.user_id} onClick={() => onSelect(m)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            background: 'transparent', border: 'none', cursor: 'pointer', color: T.textPrimary,
            fontSize: 12, textAlign: 'left',
          }}
          onMouseEnter={e => e.currentTarget.style.background = T.surface2}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <Avatar userId={m.user_id} first={m.first_name} last={m.last_name} size={24} />
          <span style={{ fontWeight: 600 }}>{m.first_name} {m.last_name}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────
export default function TeamChatPanel({ selectedRecord, onUnread }) {
  const { user } = useAuthStore();

  // Channel state
  const [channelId, setChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Input state
  const [inputValue, setInputValue] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // UI state
  const [showMembers, setShowMembers] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [sending, setSending] = useState(false);

  // DM state
  const [dmMode, setDmMode] = useState(false);
  const [dmSearch, setDmSearch] = useState('');
  const [dmUsers, setDmUsers] = useState([]);
  const [dmSearching, setDmSearching] = useState(false);
  const [activeDm, setActiveDm] = useState(null); // { channelId, user }

  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // ─── Channel loading ─────────────────────────
  const activeChannelId = dmMode && activeDm ? activeDm.channelId : channelId;

  useEffect(() => {
    if (dmMode) return;
    if (!selectedRecord?.record_id) { setChannelId(null); setMessages([]); setMembers([]); return; }
    setLoading(true);
    api.get(`/chat/channels/record/${selectedRecord.record_id}`)
      .then(({ data }) => {
        if (data.channel) {
          setChannelId(data.channel.id);
          setMembers(data.members || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedRecord?.record_id, dmMode]);

  // Load members for channel
  useEffect(() => {
    if (!activeChannelId) return;
    api.get(`/chat/channels/${activeChannelId}/members`)
      .then(({ data }) => setMembers(data.members || []))
      .catch(() => {});
  }, [activeChannelId]);

  // Load messages
  useEffect(() => {
    if (!activeChannelId) { setMessages([]); return; }
    setLoading(true);
    setMessages([]);
    setHasMore(false);
    api.get(`/chat/channels/${activeChannelId}/messages`, { params: { limit: 50 } })
      .then(({ data }) => {
        setMessages(data.messages || []);
        setHasMore((data.messages || []).length >= 50);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Mark as read
    api.post(`/chat/channels/${activeChannelId}/read`).catch(() => {});
  }, [activeChannelId]);

  // Poll for new messages every 4s
  useEffect(() => {
    if (!activeChannelId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/chat/channels/${activeChannelId}/messages`, { params: { limit: 50 } });
        const fresh = data.messages || [];
        setMessages(prev => {
          if (fresh.length === prev.length && fresh[fresh.length - 1]?.id === prev[prev.length - 1]?.id) return prev;
          const newMsgs = fresh.filter(m => !prev.find(p => p.id === m.id));
          if (onUnread && newMsgs.some(m => m.sender_id !== user?.id)) {
            onUnread(newMsgs.filter(m => m.sender_id !== user?.id).length);
          }
          return fresh;
        });
      } catch (e) { /* silent */ }
    };
    pollRef.current = setInterval(poll, 4000);
    return () => clearInterval(pollRef.current);
  }, [activeChannelId, user?.id, onUnread]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, autoScroll]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 60);
  }, []);

  // Load older messages
  const loadMore = useCallback(async () => {
    if (!activeChannelId || !hasMore || loading) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/chat/channels/${activeChannelId}/messages`, { params: { limit: 50, before: oldest.created_at } });
      const older = data.messages || [];
      if (older.length < 50) setHasMore(false);
      setMessages(prev => [...older, ...prev]);
    } catch (e) { /* */ }
    finally { setLoading(false); }
  }, [activeChannelId, hasMore, loading, messages]);

  // ─── Send ─────────────────────────────────────
  const handleSend = async () => {
    const content = inputValue.trim();
    if ((!content && attachments.length === 0) || !activeChannelId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/channels/${activeChannelId}/messages`, {
        content: content || '[Attachment]',
        is_urgent: isUrgent,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setMessages(prev => {
        if (prev.find(m => m.id === data.message?.id)) return prev;
        return [...prev, data.message];
      });
      setInputValue('');
      setAttachments([]);
      setIsUrgent(false);
      setAutoScroll(true);
    } catch (e) { console.error('[TeamChat] send error:', e); }
    finally { setSending(false); }
  };

  // ─── Edit / Delete ────────────────────────────
  const handleEditSubmit = async () => {
    if (!editContent.trim() || !editingId) return;
    try {
      await api.put(`/chat/messages/${editingId}`, { content: editContent.trim() });
      setMessages(prev => prev.map(m => m.id === editingId ? { ...m, content: editContent.trim(), is_edited: true } : m));
      setEditingId(null);
      setEditContent('');
    } catch (e) { /* */ }
  };

  const handleDelete = async (msgId) => {
    try {
      await api.delete(`/chat/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (e) { /* */ }
  };

  // ─── File upload ──────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChannelId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/chat/channels/${activeChannelId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachments(prev => [...prev, data.attachment]);
    } catch (e) { /* */ }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  // ─── @mention ─────────────────────────────────
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const atMatch = before.match(/@([^@\s]*)$/);
    if (atMatch) { setMentionOpen(true); setMentionFilter(atMatch[1]); }
    else { setMentionOpen(false); setMentionFilter(''); }
  };

  const handleMentionSelect = (member) => {
    const cursor = inputRef.current?.selectionStart || inputValue.length;
    const before = inputValue.slice(0, cursor);
    const atPos = before.lastIndexOf('@');
    const mention = `@[${member.first_name} ${member.last_name}](${member.user_id}) `;
    setInputValue(inputValue.slice(0, atPos) + mention + inputValue.slice(cursor));
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); editingId ? handleEditSubmit() : handleSend(); }
    if (e.key === 'Escape') {
      if (editingId) { setEditingId(null); setEditContent(''); }
      if (mentionOpen) setMentionOpen(false);
    }
  };

  // ─── DM search ────────────────────────────────
  useEffect(() => {
    if (!dmMode || dmSearch.length < 2) { setDmUsers([]); return; }
    const t = setTimeout(async () => {
      setDmSearching(true);
      try {
        const { data } = await api.get('/chat/users', { params: { q: dmSearch } });
        setDmUsers(data.users || []);
      } catch (e) { /* */ }
      finally { setDmSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [dmSearch, dmMode]);

  const openDm = async (targetUser) => {
    try {
      const res = await api.post('/chat/channels/dm', { userId: targetUser.id || targetUser.user_id });
      const ch = res.data?.channel || res.data;
      const chId = ch?.id || ch?.channel_id;
      if (chId) {
        setActiveDm({ channelId: chId, user: targetUser });
      } else {
        console.error('[DM] No channel ID in response:', res.data);
      }
    } catch (e) { console.error('[DM] error:', e); }
  };

  // ─── Group messages by date ───────────────────
  const grouped = [];
  let lastDate = '';
  for (const msg of messages) {
    const dk = new Date(msg.created_at).toDateString();
    if (dk !== lastDate) { grouped.push({ type: 'sep', date: msg.created_at, key: 'sep-' + dk }); lastDate = dk; }
    grouped.push({ type: 'msg', ...msg, key: msg.id });
  }

  // ─── No record + no DM ───────────────────────
  if (!selectedRecord && !dmMode) {
    return (
      <div style={{ background: T.panelBg, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <span style={{ fontSize: 48, opacity: 0.3 }}>&#128172;</span>
        <p style={{ color: T.textMuted, fontSize: 13 }}>Select a case to start team chat</p>
        <p style={{ color: T.textMuted, fontSize: 11 }}>or use DM to message a colleague</p>
        <button onClick={() => setDmMode(true)} style={{
          background: T.surface2, border: `1px solid ${T.panelBorder}`, color: T.textGold,
          fontSize: 12, padding: '8px 16px', borderRadius: 20, cursor: 'pointer',
        }}>Open DMs</button>
      </div>
    );
  }

  // ─── DM list view ─────────────────────────────
  if (dmMode && !activeDm) {
    return (
      <div style={{ background: T.panelBg, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ ...headerStyle, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setDmMode(false)} style={iconBtnStyle}><ArrowLeft size={16} /></button>
            <p style={{ color: T.textPrimary, fontSize: 13, fontWeight: 600, margin: 0 }}>Direct Messages</p>
          </div>
        </div>
        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.panelBorder}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.inputBg, border: `1px solid ${T.inputBorder}`, borderRadius: 12, padding: '6px 10px' }}>
            <Search size={14} style={{ color: T.textMuted, flexShrink: 0 }} />
            <input
              value={dmSearch}
              onChange={e => setDmSearch(e.target.value)}
              placeholder="Search people..."
              style={{ flex: 1, background: 'transparent', color: T.textPrimary, fontSize: 12, border: 'none', outline: 'none' }}
            />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {dmSearching && <p style={{ color: T.textMuted, fontSize: 11, textAlign: 'center', padding: 16 }}>Searching...</p>}
          {!dmSearching && dmSearch.length >= 2 && dmUsers.length === 0 && (
            <p style={{ color: T.textMuted, fontSize: 11, textAlign: 'center', padding: 16 }}>No users found</p>
          )}
          {dmUsers.map(u => (
            <button key={u.id || u.user_id} onClick={() => openDm(u)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                background: 'transparent', border: 'none', cursor: 'pointer', color: T.textPrimary, textAlign: 'left',
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.surface2}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Avatar userId={u.id || u.user_id} first={u.first_name} last={u.last_name} size={32} />
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{u.first_name} {u.last_name}</p>
                <p style={{ margin: 0, fontSize: 10, color: T.textMuted }}>{u.email}</p>
              </div>
            </button>
          ))}
          {dmSearch.length < 2 && (
            <p style={{ color: T.textMuted, fontSize: 11, textAlign: 'center', padding: 16 }}>Type at least 2 characters to search</p>
          )}
        </div>
      </div>
    );
  }

  // ─── Main chat view (case channel or DM) ──────
  const headerTitle = activeDm ? `${activeDm.user.first_name} ${activeDm.user.last_name}` : 'Team Chat';
  const headerSub = activeDm
    ? 'Direct Message'
    : `${selectedRecord?.title || ''}${members.length > 0 ? ` \u00B7 ${members.length} member${members.length !== 1 ? 's' : ''}` : ''}`;

  return (
    <div style={{ background: T.panelBg, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{`.tc-msg:hover .tc-actions { opacity: 1 !important; }`}</style>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          {activeDm && (
            <button onClick={() => setActiveDm(null)} style={iconBtnStyle}><ArrowLeft size={16} /></button>
          )}
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: T.textGold, fontSize: 14 }}>{activeDm ? '\u{1F464}' : '\u{1F4AC}'}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: T.textPrimary, fontSize: 13, fontWeight: 600, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{headerTitle}</p>
            <p style={{ color: T.textMuted, fontSize: 10, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{headerSub}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {!activeDm && (
            <button onClick={() => setShowMembers(v => !v)} style={{ ...iconBtnStyle, color: showMembers ? T.textGold : T.textMuted }} title="Members">
              <Users size={15} />
              <span style={{ fontSize: 9, marginLeft: 2 }}>{members.length}</span>
            </button>
          )}
          <button onClick={() => { setDmMode(v => !v); setActiveDm(null); }}
            style={{ ...iconBtnStyle, color: dmMode ? T.textGold : T.textMuted }} title="Direct Messages">
            <MessageSquare size={15} />
          </button>
        </div>
      </div>

      {/* Members dropdown */}
      {showMembers && !activeDm && (
        <div style={{ borderBottom: `1px solid ${T.panelBorder}`, background: T.surface1, padding: '8px 16px', maxHeight: 160, overflowY: 'auto', flexShrink: 0 }}>
          {members.filter(m => m.user_id !== user?.id).map(m => (
            <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <Avatar userId={m.user_id} first={m.first_name} last={m.last_name} size={24} />
              <span style={{ color: T.textPrimary, fontSize: 12 }}>{m.first_name} {m.last_name}</span>
            </div>
          ))}
          {members.filter(m => m.user_id !== user?.id).length === 0 && <p style={{ color: T.textMuted, fontSize: 11 }}>No other members</p>}
        </div>
      )}

      {/* Messages */}
      <div ref={containerRef} onScroll={handleScroll}
        style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {hasMore && messages.length > 0 && (
          <button onClick={loadMore} disabled={loading}
            style={{ background: 'transparent', border: 'none', color: T.textGold, fontSize: 11, cursor: 'pointer', padding: '8px', textAlign: 'center', opacity: loading ? 0.5 : 1 }}>
            {loading ? 'Loading...' : 'Load older messages'}
          </button>
        )}
        {loading && messages.length === 0 && <p style={{ color: T.textMuted, fontSize: 12, textAlign: 'center', padding: '32px 0' }}>Loading messages...</p>}
        {!loading && messages.length === 0 && <p style={{ color: T.textMuted, fontSize: 12, textAlign: 'center', padding: '32px 0' }}>No messages yet. Start the conversation!</p>}

        {grouped.map(item => {
          if (item.type === 'sep') {
            return (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <div style={{ flex: 1, height: 1, background: T.panelBorder }} />
                <span style={{ color: T.textMuted, fontSize: 10, fontWeight: 500, flexShrink: 0 }}>{fmtDateSep(item.date)}</span>
                <div style={{ flex: 1, height: 1, background: T.panelBorder }} />
              </div>
            );
          }

          const msg = item;
          const isMe = msg.sender_id === user?.id;
          const senderName = `${msg.sender_first || msg.first_name || ''} ${msg.sender_last || msg.last_name || ''}`.trim() || 'Unknown';
          const msgAttachments = typeof msg.attachments === 'string' ? JSON.parse(msg.attachments || '[]') : (msg.attachments || []);
          const isPrivateMsg = msg.visibility === 'private';
          const isUrgentMsg = msg.is_urgent;

          return (
            <div key={msg.key} className="tc-msg"
              style={{ display: 'flex', gap: 8, padding: '4px 0', flexDirection: isMe ? 'row-reverse' : 'row' }}>
              {!isMe && <Avatar userId={msg.sender_id} first={msg.sender_first || msg.first_name} last={msg.sender_last || msg.last_name} />}
              <div style={{ maxWidth: '80%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                {/* Name + time + badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>{isMe ? 'You' : senderName}</span>
                  <span style={{ fontSize: 10, color: '#5a4a30' }}>{fmtTime(msg.created_at)}</span>
                  {isPrivateMsg && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, color: T.textGold }}>
                      <Lock size={10} /> Private
                    </span>
                  )}
                  {isUrgentMsg && (
                    <span style={{ fontSize: 9, padding: '1px 6px', background: T.urgentBg, color: '#d94f4f', border: `1px solid ${T.urgentBorder}`, borderRadius: 10, fontWeight: 600 }}>URGENT</span>
                  )}
                </div>

                {/* Bubble */}
                <div style={{
                  position: 'relative',
                  background: isPrivateMsg ? T.privateBg : (isMe ? T.outgoingBg : T.surface1),
                  border: `1px solid ${isPrivateMsg ? T.privateBorder : (isMe ? T.outgoingBorder : T.panelBorder)}`,
                  borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  padding: '8px 12px',
                }}>
                  {editingId === msg.id ? (
                    <div>
                      <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                        onKeyDown={handleKeyDown} autoFocus rows={2}
                        style={{ width: '100%', background: T.surface2, border: `1px solid ${T.panelBorder}`, color: T.textPrimary, fontSize: 12, borderRadius: 8, padding: '6px 8px', resize: 'none', outline: 'none' }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <button onClick={handleEditSubmit} style={{ fontSize: 11, color: T.textGold, background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Save</button>
                        <button onClick={() => { setEditingId(null); setEditContent(''); }} style={{ fontSize: 11, color: T.textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap', color: T.textPrimary, fontSize: 13, lineHeight: 1.5 }}>{renderContent(msg.content)}</div>
                  )}
                  {msg.is_edited && !editingId && (
                    <span style={{ fontSize: 10, color: T.textMuted, fontStyle: 'italic', marginLeft: 4 }}>(edited)</span>
                  )}
                  {/* Attachments */}
                  {msgAttachments.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {msgAttachments.map((att, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.surface2, border: `1px solid ${T.panelBorder}`, borderRadius: 8, padding: '6px 8px', fontSize: 11 }}>
                          <Paperclip size={12} style={{ color: T.textMuted, flexShrink: 0 }} />
                          <span style={{ color: T.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
                          <span style={{ color: T.textMuted, flexShrink: 0 }}>{fmtFileSize(att.size)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Hover actions */}
                {editingId !== msg.id && (isMe || user?.is_admin) && (
                  <div className="tc-actions" style={{ display: 'flex', gap: 2, marginTop: 2, opacity: 0, transition: 'opacity 0.15s' }}>
                    {isMe && (
                      <button onClick={() => { setEditingId(msg.id); setEditContent(msg.content); }}
                        style={{ ...tinyBtnStyle }} title="Edit"><Pencil size={11} /></button>
                    )}
                    <button onClick={() => handleDelete(msg.id)}
                      style={{ ...tinyBtnStyle, color: '#7a2424' }} title="Delete"><Trash2 size={11} /></button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom */}
      {!autoScroll && messages.length > 0 && (
        <div style={{ position: 'relative' }}>
          <button onClick={() => { setAutoScroll(true); messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
            style={{ position: 'absolute', bottom: 8, right: 16, padding: 6, background: T.surface1, border: `1px solid ${T.panelBorder}`, borderRadius: '50%', cursor: 'pointer', color: T.textMuted, zIndex: 10 }}>
            <ChevronDown size={16} />
          </button>
        </div>
      )}

      {/* Pending attachments */}
      {attachments.length > 0 && (
        <div style={{ padding: '6px 12px', borderTop: `1px solid ${T.panelBorder}`, background: T.surface1, flexShrink: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {attachments.map((att, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.surface2, border: `1px solid ${T.panelBorder}`, borderRadius: 8, padding: '4px 8px', fontSize: 11 }}>
                <Paperclip size={11} style={{ color: T.textMuted }} />
                <span style={{ color: T.textPrimary, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
                <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.textMuted, padding: 0 }}>
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div style={{ padding: '8px 12px', borderTop: `1px solid ${T.panelBorder}`, background: T.panelBg, flexShrink: 0, position: 'relative' }}>
        {mentionOpen && <MentionDropdown members={members} filter={mentionFilter} onSelect={handleMentionSelect} />}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading || !activeChannelId}
            style={{ ...iconBtnStyle, opacity: uploading ? 0.5 : 1 }} title="Attach file">
            <Paperclip size={15} />
          </button>
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', background: T.inputBg, border: `1px solid ${T.inputBorder}`, borderRadius: 14, padding: '6px 10px' }}>
            <textarea ref={inputRef} value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown}
              placeholder={activeDm ? `Message ${activeDm.user.first_name}...` : 'Message the team...'}
              disabled={sending || !activeChannelId}
              rows={1}
              style={{
                flex: 1, background: 'transparent', color: T.textPrimary, fontSize: 13, border: 'none', outline: 'none',
                resize: 'none', lineHeight: '20px', minHeight: '20px', maxHeight: '80px',
              }}
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px'; }}
            />
          </div>
          {!activeDm && (
            <button onClick={() => setIsUrgent(v => !v)}
              style={{ ...iconBtnStyle, color: isUrgent ? '#d94f4f' : T.textMuted }} title="Urgent">
              <Flag size={14} />
            </button>
          )}
          <button onClick={handleSend} disabled={(!inputValue.trim() && !attachments.length) || sending || !activeChannelId}
            style={{ ...iconBtnStyle, color: inputValue.trim() ? T.textGold : '#3d2a1a', opacity: sending ? 0.5 : 1 }} title="Send">
            <Send size={15} />
          </button>
        </div>
        {uploading && <span style={{ fontSize: 10, color: T.textMuted, marginTop: 2, display: 'block' }}>Uploading...</span>}
      </div>
    </div>
  );
}

// ─── Shared styles ──────────────────────────────────────
const headerStyle = {
  background: T.panelBg, borderBottom: `1px solid ${T.panelBorder}`,
  padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
};

const iconBtnStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: T.textMuted, padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center',
  transition: 'color 0.2s', flexShrink: 0,
};

const tinyBtnStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: T.textMuted, padding: 2, borderRadius: 4, display: 'flex', alignItems: 'center',
};
